import {
  SuggestedPriceExplanationCategory,
  SuggestedPriceExplanationFactor,
} from '@features/suggested-price-column/api/get-suggested-price-explanation';

export interface SuggestedPriceFactorCategoryGroup {
  categoryKey: SuggestedPriceExplanationCategory;
  categoryLabel: string;
  reasons: string[];
}

/**
 * Folds one direction's factors into a single group per category, each carrying its reasons as
 * already-resolved sentences.
 *
 * Several factors can share a category key, and rendering them one per row repeats the same
 * heading down the drawer. Grouping gives each category a single row to list its reasons under.
 *
 * Callers pass one direction's factors at a time. Folding the whole list would merge a category
 * that pushed the price both ways into one row, filed under whichever heading it reached first.
 *
 * Groups come back in the order their first factor arrived and reasons in the order they were
 * given, so whatever ranking the caller was handed survives the fold.
 *
 * Reasons are deduped on the resolved sentence rather than on `copy`, so two factors differing
 * only in their `{{amount}}` still read as two bullets while genuinely identical text appears
 * once.
 */
export const groupFactorsByCategory = (
  factors: SuggestedPriceExplanationFactor[],
  resolveCopy: (factor: SuggestedPriceExplanationFactor) => string,
): SuggestedPriceFactorCategoryGroup[] => {
  const groups = new Map<SuggestedPriceExplanationCategory, SuggestedPriceFactorCategoryGroup>();

  factors.forEach(factor => {
    const reason = resolveCopy(factor);
    const group = groups.get(factor.category_key);

    if (!group) {
      groups.set(factor.category_key, {
        categoryKey: factor.category_key,
        categoryLabel: factor.category_label,
        reasons: [reason],
      });

      return;
    }

    if (!group.reasons.includes(reason)) group.reasons.push(reason);
  });

  return [...groups.values()];
};
